import { Box, Flex, IconButton, Text, useToast } from '@chakra-ui/react'
import { createColumnHelper } from '@tanstack/react-table'
import { AxiosError } from 'axios'
import { ROLE } from 'constant'
import { CopyIcon } from 'components/atoms/Icons'
import { DataTable, InputField, SelectField } from 'components/atoms'
import { TextOverflowTooltip } from 'components/fromAdvisor/elements'
import { Button } from 'components/fromAdvisor/elements/Button'
import { FormikProvider, useFormik } from 'formik'
import moment from 'moment'
import 'moment-timezone'
import { FC, useCallback, useEffect, useState } from 'react'
import { accessLogServices, userAdminServices } from 'services'
import {
  ExportPageViewLogsRequest,
  GetPageViewLogResponse,
  GetPageViewLogsRequest,
} from 'services/accessLogServices'
import { TenantResponse } from 'services/userServices'
import { PaginateResponse } from 'types/conmon'
import { downloadBlobAsFile, resolveCsvFilename } from 'utils/csvExport'

const DEFAULT_SORT_BY = 'accessed_at:desc'

type AccessLogFilterValues = {
  role: ROLE | ''
  tenant_id: string | number
  user_name: string
  email: string
  from: string
  to: string
}

const getDefaultDateRange = (): Pick<AccessLogFilterValues, 'from' | 'to'> => ({
  from: moment().subtract(7, 'days').format('YYYY-MM-DD'),
  to: moment().format('YYYY-MM-DD'),
})

const initialFilterValues: AccessLogFilterValues = {
  role: '',
  tenant_id: '',
  user_name: '',
  email: '',
  ...getDefaultDateRange(),
}

const formatDateFilter = (value: string): string | undefined => {
  if (!value) {
    return undefined
  }

  if (!moment(value, 'YYYY-MM-DD', true).isValid()) {
    return undefined
  }

  return value
}

const isDateRangeInvalid = (
  filters: Pick<AccessLogFilterValues, 'from' | 'to'>,
): boolean => {
  const fromDate = moment(filters.from, 'YYYY-MM-DD', true)
  const toDate = moment(filters.to, 'YYYY-MM-DD', true)

  if (!fromDate.isValid() || !toDate.isValid()) {
    return false
  }

  return fromDate.isAfter(toDate, 'day')
}

const normalizeFilterParams = (
  filters: AccessLogFilterValues,
): Omit<GetPageViewLogsRequest, 'page' | 'page_size' | 'sort_by'> => {
  return {
    role: filters.role || undefined,
    tenant_id: filters.tenant_id || undefined,
    user_name: filters.user_name.trim() || undefined,
    email: filters.email.trim() || undefined,
    from: formatDateFilter(filters.from),
    to: formatDateFilter(filters.to),
  }
}

/**
 * AccessLog Page
 * @constructor
 */
export const AccessLog: FC = () => {
  const toast = useToast()
  const [tenantList, setTenantList] = useState<TenantResponse[]>([])
  const [isExporting, setIsExporting] = useState(false)
  const [pagination, setPagination] = useState<PaginateResponse>({
    page_size: 15,
    page: 1,
    page_count: 1,
    total_count: 0,
  })
  const [appliedFilters, setAppliedFilters] = useState<
    Omit<GetPageViewLogsRequest, 'page' | 'page_size' | 'sort_by'>
  >(normalizeFilterParams(initialFilterValues))
  const [dataAccessLog, setDataAccessLog] = useState<{
    loading: boolean
    data: GetPageViewLogResponse[]
    last_updated?: string
  }>({ loading: false, data: [] })
  const columnHelper = createColumnHelper<GetPageViewLogResponse>()
  const latestUpdatedDate = dataAccessLog.last_updated
    ? moment(dataAccessLog.last_updated).format('YYYY/MM/DD')
    : dataAccessLog.data.length > 0
    ? moment
        .utc(dataAccessLog.data[0].accessed_at)
        .tz('Asia/Tokyo')
        .format('YYYY/MM/DD')
    : '-'

  const roleOptions = [
    { value: ROLE.ADMIN, label: '管理者' },
    { value: ROLE.ADVISOR, label: '一般ユーザー' },
  ]

  const getTenantList = useCallback(async () => {
    try {
      const { data } = await userAdminServices.getListTenant()
      setTenantList(data)
    } catch (error) {
      if (
        process.env.NODE_ENV === 'development' &&
        error instanceof AxiosError
      ) {
        console.log('error', error)
      }
    }
  }, [])

  const getPageViewLogs = useCallback(async () => {
    try {
      setDataAccessLog(prev => ({ ...prev, loading: true }))
      const params: GetPageViewLogsRequest = {
        page_size: pagination.page_size,
        page: pagination.page,
        sort_by: DEFAULT_SORT_BY,
        ...appliedFilters,
      }

      const { data, last_updated, paginate } =
        await accessLogServices.getPageViewLogs(params)

      setDataAccessLog(prev => ({
        ...prev,
        data,
        loading: false,
        last_updated,
      }))

      if (paginate) {
        setPagination(paginate)
      }
    } catch (error) {
      setDataAccessLog(prev => ({ ...prev, loading: false }))
      if (
        process.env.NODE_ENV === 'development' &&
        error instanceof AxiosError
      ) {
        console.log('error', error)
      }
    }
  }, [appliedFilters, pagination.page, pagination.page_size])

  useEffect(() => {
    getTenantList()
  }, [getTenantList])

  useEffect(() => {
    getPageViewLogs()
  }, [getPageViewLogs])

  const formik = useFormik({
    initialValues: initialFilterValues,
    onSubmit: async () => {},
  })
  const hasDateRangeError = isDateRangeInvalid(formik.values)

  const applyFilter = () => {
    if (hasDateRangeError) {
      return
    }

    setPagination(prev => ({ ...prev, page: 1 }))
    setAppliedFilters(normalizeFilterParams(formik.values))
  }

  const resetFilter = () => {
    const nextValues: AccessLogFilterValues = {
      ...initialFilterValues,
      ...getDefaultDateRange(),
    }
    formik.resetForm({ values: nextValues })
    setPagination(prev => ({ ...prev, page: 1 }))
    setAppliedFilters(normalizeFilterParams(nextValues))
  }

  const onEnterApplyFilter: React.KeyboardEventHandler<
    HTMLInputElement
  > = event => {
    if (event.key === 'Enter') {
      applyFilter()
    }
  }

  const handleDownloadCsv = useCallback(async () => {
    try {
      setIsExporting(true)
      const exportFilters: ExportPageViewLogsRequest = {
        sort_by: DEFAULT_SORT_BY,
        ...appliedFilters,
      }
      const { blob, contentDisposition } =
        await accessLogServices.exportPageViewLogsCsv(exportFilters)
      const fallbackFilename = `access_log_${moment().format(
        'YYYYMMDD_HHmmss',
      )}.csv`
      const filename = resolveCsvFilename(contentDisposition, fallbackFilename)

      downloadBlobAsFile(blob, filename)

      toast({
        status: 'success',
        description: 'CSVをダウンロードしました',
        position: 'top',
      })
    } catch (error) {
      if (
        process.env.NODE_ENV === 'development' &&
        error instanceof AxiosError
      ) {
        console.log('error', error)
      }
      toast({
        status: 'error',
        description: 'CSVのダウンロードに失敗しました',
        position: 'top',
      })
    } finally {
      setIsExporting(false)
    }
  }, [appliedFilters, toast])

  const columns = [
    columnHelper.accessor('accessed_at', {
      id: 'accessed_at',
      cell: info => {
        const japanDateTime = moment.utc(info.getValue()).tz('Asia/Tokyo')
        return (
          <TextOverflowTooltip
            fontSize="sm"
            fontWeight={'normal'}
            value={japanDateTime.format('YYYY/MM/DD HH:mm:ss')}
            width="100%"
          />
        )
      },
      header: '日時',
      size: 170,
      meta: {
        minWidthColumn: '170px',
      },
    }),
    columnHelper.accessor('user_name', {
      id: 'user_name',
      cell: info => (
        <TextOverflowTooltip
          color="blue.900"
          fontSize="sm"
          fontWeight={'bold'}
          value={info.getValue() || '-'}
        />
      ),
      header: 'ユーザー',
      size: 190,
      meta: {
        minWidthColumn: '190px',
      },
    }),
    columnHelper.accessor('tenant_name', {
      id: 'tenant',
      cell: info => {
        const tenantName = info.row.original.tenant_name
        const tenantCode = info.row.original.tenant_code
        return (
          <TextOverflowTooltip
            fontSize="sm"
            fontWeight={'normal'}
            value={tenantName || tenantCode || '-'}
          />
        )
      },
      header: 'テナント',
      size: 170,
      meta: {
        minWidthColumn: '170px',
      },
    }),
    columnHelper.accessor('role', {
      id: 'role',
      cell: info => (
        <TextOverflowTooltip
          fontSize="sm"
          fontWeight={'normal'}
          value={info.getValue() || '-'}
        />
      ),
      header: '権限',
      size: 110,
      meta: {
        minWidthColumn: '110px',
      },
    }),
    columnHelper.accessor('path', {
      id: 'path',
      cell: info => {
        const path = info.getValue() || '-'

        const handleCopy = async () => {
          if (path === '-') {
            return
          }
          try {
            await navigator.clipboard.writeText(path)
            toast({
              status: 'success',
              description: 'コピーしました',
              position: 'top',
            })
          } catch {
            toast({
              status: 'error',
              description: 'コピーに失敗しました',
              position: 'top',
            })
          }
        }

        return (
          <Flex alignItems="center" gap="4px">
            <TextOverflowTooltip
              fontSize="sm"
              fontWeight={'normal'}
              value={path}
            />
            <IconButton
              _hover={{ bg: 'transparent' }}
              aria-label="copy-path"
              bg="transparent"
              icon={<CopyIcon boxSize="18px" />}
              minW="28px"
              size="sm"
              onClick={handleCopy}
            />
          </Flex>
        )
      },
      header: 'パス',
      size: 320,
      meta: {
        minWidthColumn: '320px',
      },
    }),
    columnHelper.accessor('email', {
      id: 'email',
      cell: info => (
        <TextOverflowTooltip
          fontSize="sm"
          fontWeight={'normal'}
          value={info.getValue() || '-'}
        />
      ),
      header: 'メールアドレス',
      size: 240,
      meta: {
        minWidthColumn: '240px',
      },
    }),
    columnHelper.accessor('department', {
      id: 'department',
      cell: info => (
        <TextOverflowTooltip
          fontSize="sm"
          fontWeight={'normal'}
          value={info.getValue() || '-'}
        />
      ),
      header: '部署',
      size: 170,
      meta: {
        minWidthColumn: '170px',
      },
    }),
    columnHelper.accessor('position', {
      id: 'position',
      cell: info => (
        <TextOverflowTooltip
          fontSize="sm"
          fontWeight={'normal'}
          value={info.getValue() || '-'}
        />
      ),
      header: '役職',
      size: 170,
      meta: {
        minWidthColumn: '170px',
      },
    }),
  ]

  return (
    <FormikProvider value={formik}>
      <Box
        bgColor="white.50"
        borderRadius={4}
        minH="calc(100vh - 88px)"
        padding="12px 24px"
        w="100%"
      >
        <Flex align={'center'} justifyContent="space-between" mb="16px">
          <Flex alignItems="center" gap="24px">
            <Text
              fontSize="24px"
              fontWeight="700"
              letterSpacing="tighter"
              lineHeight="40px"
              minW="180px"
              padding="0 8px"
            >
              アクセスログ
            </Text>
            <Text
              fontSize="12px"
              fontWeight="400"
              lineHeight="12px"
              minW="135px"
              opacity="0.5"
            >
              最終更新日：
              {latestUpdatedDate}
            </Text>
          </Flex>
        </Flex>

        <Flex columnGap="12px" mb="16px" rowGap="12px" wrap="wrap">
          <SelectField
            height="40px"
            isInForm={false}
            name="role"
            options={roleOptions}
            placeholder="権限"
            valueOption={
              roleOptions.find(it => it.value === formik.values.role) ?? null
            }
            width="180px"
            onChange={option => {
              formik.setFieldValue('role', option?.value ?? '')
            }}
          />
          <SelectField
            height="40px"
            isInForm={false}
            name="tenant_id"
            options={tenantList.map(tenant => ({
              value: tenant.id,
              label: tenant.tenant_name,
            }))}
            placeholder="テナント"
            valueOption={(() => {
              const selectedTenant = tenantList.find(
                tenant => tenant.id === formik.values.tenant_id,
              )
              if (selectedTenant) {
                return {
                  value: selectedTenant.id,
                  label: selectedTenant.tenant_name,
                }
              }
              return null
            })()}
            width="220px"
            onChange={option => {
              formik.setFieldValue('tenant_id', option?.value ?? '')
            }}
          />
          <InputField
            height="40px"
            name="user_name"
            placeholder="ユーザー"
            width="220px"
            onChange={event =>
              formik.setFieldValue('user_name', event.target.value)
            }
            onKeyDown={onEnterApplyFilter}
          />
          <InputField
            height="40px"
            name="email"
            placeholder="メールアドレス"
            width="220px"
            onChange={event =>
              formik.setFieldValue('email', event.target.value)
            }
            onKeyDown={onEnterApplyFilter}
          />
          <InputField
            height="40px"
            isInForm={false}
            name="from"
            placeholder="開始日"
            type="date"
            value={formik.values.from}
            width="210px"
            onChange={event => formik.setFieldValue('from', event.target.value)}
            onKeyDown={onEnterApplyFilter}
          />
          <InputField
            height="40px"
            isInForm={false}
            name="to"
            placeholder="終了日"
            type="date"
            value={formik.values.to}
            width="210px"
            onChange={event => formik.setFieldValue('to', event.target.value)}
            onKeyDown={onEnterApplyFilter}
          />
          <Button
            backgroundColor="#0084FF"
            border="1px solid #0084FF"
            color="#FEFEFE"
            height="40px"
            isDisabled={hasDateRangeError}
            minW="120px"
            text="絞り込み"
            onClick={applyFilter}
          />
          <Button
            backgroundColor="#FEFEFE"
            border="1px solid #0084FF"
            color="#0084FF"
            height="40px"
            minW="120px"
            text="リセット"
            onClick={resetFilter}
          />
          <Button
            backgroundColor="#FEFEFE"
            border="1px solid #0084FF"
            color="#0084FF"
            height="40px"
            isLoading={isExporting}
            minW="160px"
            text="CSVダウンロード"
            onClick={handleDownloadCsv}
          />
          {hasDateRangeError && (
            <Text
              color="red.700"
              fontSize="sm"
              fontWeight="bold"
              lineHeight="16px"
              textAlign="center"
              w="100%"
            >
              開始日は終了日より前でなければなりません。
            </Text>
          )}
        </Flex>

        <DataTable
          bgColorTable="white.50"
          columns={columns}
          data={dataAccessLog.data}
          isPagination={true}
          isScroll={true}
          isShowAction={false}
          loadingData={dataAccessLog.loading}
          maxH="calc(100vh - 280px)"
          pagination={pagination}
          rangePage={[15, 30, 50]}
          setPagination={setPagination}
        />
      </Box>
    </FormikProvider>
  )
}
